/*
 * Homepage scroll reveal.
 *  - Direct .shopify-section children of #MainContent fade + slide up as they
 *    enter the viewport.
 *  - Sections that contain card grids (product-card, .collection-card) opt out
 *    of the section-level fade and instead stagger their cards in one-by-one.
 *
 * Uses scroll / resize / load events rather than IntersectionObserver — IO
 * proved unreliable on this theme (likely interacting with view-transitions
 * and lazy-load observers), and a scroll handler with rAF throttling has the
 * same perf profile in practice.
 *
 * Initial hidden state is applied by scroll-animations.css when
 * <html class="scroll-reveal-active"> is set.
 */
(function () {
  if (!document.documentElement.classList.contains('scroll-reveal-active')) return;

  // .collection-links__link is intentionally NOT in this list — the spotlight
  // layout has its own opacity-based dim/highlight UX that we must not fight.
  // That section falls back to a section-level fade.
  const STAGGER_SELECTOR = 'product-card, .collection-card';
  // Cap per-item delay so a long grid doesn't leave the last item waiting
  // an absurd amount of time after it scrolls into view.
  const MAX_STAGGER_INDEX = 6;
  // Trigger when the element's top is this many pixels above the viewport
  // bottom — gives a small "wait until properly in view" margin.
  const TRIGGER_OFFSET = 60;

  function reveal(el) {
    el.classList.add('scroll-reveal-in');
  }

  function inView(el) {
    const r = el.getBoundingClientRect();
    return r.top < window.innerHeight - TRIGGER_OFFSET && r.bottom > 0;
  }

  function init() {
    // Signal to CSS that JS is alive so the no-JS fallback animation stays off.
    document.documentElement.classList.add('scroll-reveal-js');

    const main = document.getElementById('MainContent');
    if (!main) return;

    const sections = Array.from(
      main.querySelectorAll(':scope > .shopify-section')
    );
    if (!sections.length) return;

    // Tag stagger items and remember which sections own them.
    const staggerItems = Array.from(main.querySelectorAll(STAGGER_SELECTOR));
    const sectionsToSkip = new Set();
    staggerItems.forEach((item) => {
      const section = item.closest('#MainContent > .shopify-section');
      if (section) sectionsToSkip.add(section);
      item.classList.add('scroll-stagger-item');
    });

    // Index stagger items by DOM order within their section. This handles
    // carousels where each card is wrapped in its own list-item div — they
    // aren't direct siblings of each other but DOM order matches visual order.
    sectionsToSkip.forEach((section) => {
      const items = Array.from(section.querySelectorAll('.scroll-stagger-item'));
      items.forEach((item, idx) => {
        item.style.setProperty(
          '--scroll-stagger-i',
          Math.min(idx, MAX_STAGGER_INDEX - 1)
        );
      });
      section.classList.add('scroll-reveal-skip');
    });

    const pending = [
      ...sections.filter((s) => !sectionsToSkip.has(s)),
      ...staggerItems,
    ];

    function check() {
      for (let i = pending.length - 1; i >= 0; i--) {
        if (inView(pending[i])) {
          reveal(pending[i]);
          pending.splice(i, 1);
        }
      }
    }

    let rafId = null;
    function schedule() {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        check();
        if (!pending.length) {
          window.removeEventListener('scroll', schedule);
          window.removeEventListener('resize', schedule);
          window.removeEventListener('load', schedule);
        }
      });
    }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    // Images and webfonts can shift layout — re-check once they've loaded.
    window.addEventListener('load', schedule);

    // Reveal anything that's already in view on first paint.
    check();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
